// frontend/src/components/AIRecommender.js
import React, { useState, useRef, useCallback } from 'react';
import { Link } from '../routing';
import { Sparkles, Camera, X, Loader, RefreshCw } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import api, { AI_TIMEOUT } from '../utils/api';
import { PLACEHOLDER_IMG } from '../config';
import theme from '../theme';

// Must equal MAX_BYTES in backend/routes/ai.js, which is 3.5 MB and not 5 MB
// because base64 inflates the payload by a third and the model API's limit
// applies to the ENCODED bytes. The client said 5 MB, multer aborted at 3.5, and
// the error message repeated the 5 MB figure — so a 4.2 MB phone photo failed,
// and failed identically on every retry, at the first thing a visitor tries.
// backend/tests/hardening.test.js asserts the two cannot drift apart.
const MAX_BYTES     = Math.floor(3.5 * 1024 * 1024);
const MAX_MB_LABEL  = '3.5';
// Longest edge after downscaling. Above this the model gets no extra detail it
// can use, and a modern phone photo is 3–4× this on its long side.
const MAX_EDGE_PX   = 1568;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Shrink an oversized photo instead of refusing it.
 *
 * Rejecting a 4.2 MB camera photo is technically correct and completely useless:
 * the visitor has no way to make the file smaller on a phone, so the feature is
 * a dead end for exactly the images it exists to look at. Anything already small
 * enough is passed through untouched.
 */
async function shrinkIfNeeded(file) {
  if (file.size <= MAX_BYTES) return file;

  const bitmap = await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
    img.src = url;
  });

  const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  // Step the quality down until it fits. JPEG regardless of the input format —
  // a re-encoded PNG of a photograph is usually larger than the original.
  for (const quality of [0.85, 0.7, 0.55, 0.4]) {
    /* eslint-disable no-await-in-loop */
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= MAX_BYTES) {
      return new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    }
  }
  return null;
}

export default function AIRecommender() {
  const { t, i18n } = useTranslation();
  const isEl = i18n.language === 'el';

  const [photo, setPhoto]   = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(true);
  // Explicit consent, as its own act, before a file picker can even open.
  //
  // The photo is of a person's face, and what the model is asked to read off it
  // is hair colour and TEXTURE. Hair texture correlates strongly enough with
  // ethnic origin that the analysis is capable of revealing it, and the CJEU
  // reads "revealing" in Art. 9 broadly enough to cover inference rather than
  // only the stated purpose (C-184/20 Vyriausioji, C-252/21 Meta). Art. 9(2)(a)
  // wants EXPLICIT consent for that, which choosing to upload a file is not:
  // uploading is an unambiguous act about uploading, and the privacy note beside
  // it was information, not agreement. A separate, unticked, non-blocking box is
  // the smallest thing that actually is one.
  const [consent, setConsent] = useState(false);

  const inputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { setError(t('aiAdvisor.errorType')); return; }
    setError(null);
    setResult(null);
    let usable = file;
    if (file.size > MAX_BYTES) {
      try {
        usable = await shrinkIfNeeded(file);
      } catch (_) {
        usable = null;
      }
      if (!usable) { setError(t('aiAdvisor.errorSize', { mb: MAX_MB_LABEL })); return; }
    }
    setPhoto({ file: usable, preview: URL.createObjectURL(usable) });
  }, [t]);

  const onInputChange = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const clearPhoto = () => {
    if (photo?.preview) URL.revokeObjectURL(photo.preview);
    setPhoto(null); setResult(null); setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const analyse = async () => {
    if (!photo) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const form = new FormData();
      form.append('photo', photo.file);
      const data = await api.post('/ai/recommend', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // A photo upload followed by a vision-model call. The global 15s
        // aborted this routinely, and an abort looks exactly like a failure of
        // the feature rather than of the clock.
        timeout: AI_TIMEOUT,
      });
      setResult(data);
    } catch (err) {
      // The api interceptor rejects with a FLATTENED { ...body, status }, so
      // err.response is always undefined here and none of these branches could
      // ever match — every failure fell through to the generic message, leaving
      // someone with an oversized photo no idea they just needed a smaller one.
      const code = err?.error;
      if (code === 'AI_NOT_CONFIGURED') setError(t('aiAdvisor.noKey'));
      else if (code === 'FILE_TOO_LARGE')  setError(t('aiAdvisor.errorSize'));
      else if (code === 'INVALID_TYPE')    setError(t('aiAdvisor.errorType'));
      else setError(t('aiAdvisor.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  if (!expanded) {
    return (
      <div className="ai-tease-wrap">
        <button onClick={() => setExpanded(true)} className="ai-tease-btn">
          <Sparkles size={16} style={{ color: theme.gold, flexShrink: 0 }} />
          <span className="ai-tease-title">{t('aiAdvisor.title')}</span>
          <span className="ai-tease-badge">{t('aiAdvisor.badge')} ↓</span>
        </button>
      </div>
    );
  }

  return (
    <div className="ai-container">
      <button onClick={() => setExpanded(false)} className="ai-close">✕</button>

      {/* Header */}
      <div className="ai-header">
        <div className="ai-badge-row">
          <Sparkles size={13} /> {t('aiAdvisor.badge')}
        </div>
        <h2 className="ai-title">{t('aiAdvisor.title')}</h2>
        <p className="ai-subtitle">{t('aiAdvisor.subtitle')}</p>
      </div>

      {/* Where the photo goes, said BEFORE the file picker opens rather than
          only in a policy page nobody reads first. Art. 13 GDPR wants the
          recipient and the transfer named at the point of collection, and this
          is a photograph of a person's face leaving the EEA. */}
      <p className="ai-privacy-note">
        <Trans i18nKey="aiAdvisor.privacyNote" components={{
          policy: <Link to="/privacy" className="ai-privacy-link" />,
        }} />
      </p>

      {/* The consent itself — see the note on the `consent` state. Hidden once a
          photo is chosen, because by then it has been given and a ticked box
          sitting above the preview is just furniture. */}
      {!photo && (
        <label className="ai-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="ai-consent-box"
          />
          <span>{t('aiAdvisor.consent')}</span>
        </label>
      )}

      {/* Upload */}
      {!photo ? (
        <div
          className={`ai-dropzone${dragging ? ' ai-dropzone--active' : ''}${consent ? '' : ' ai-dropzone--locked'}`}
          // A click handler on a bare <div> is invisible to the keyboard and to
          // screen readers: this was the only way to start the flow, and it was
          // unreachable without a mouse.
          role="button"
          tabIndex={0}
          aria-label={t('aiAdvisor.uploadPrompt')}
          aria-disabled={!consent}
          onKeyDown={(e) => {
            if (!consent) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); }
          }}
          // Drop is gated too, not only the click. A drag-and-drop straight onto
          // the zone would otherwise be the one route that bypassed the consent
          // entirely — and it is the route a desktop user takes.
          onDragOver={(e) => { e.preventDefault(); if (consent) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { if (consent) onDrop(e); else e.preventDefault(); }}
          onClick={() => { if (consent) inputRef.current?.click(); }}
        >
          {/* No capture="environment": on Android that forces the rear camera and
              removes the option to pick an existing gallery photo, which is the
              one people actually want to analyse. */}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={onInputChange}
          />
          <div className="ai-dropzone-icon"><Camera size={36} /></div>
          <div className="ai-dropzone-text">{t('aiAdvisor.dragHint')}</div>
          <div className="ai-dropzone-hint">{t('aiAdvisor.uploadHint')}</div>
        </div>
      ) : (
        <div className="ai-preview-wrap">
          <div className="ai-preview-inner">
            <img src={photo.preview} alt="preview" className="ai-preview-img" />
            <button onClick={clearPhoto} className="ai-preview-clear"><X size={15} /></button>
          </div>

          {!result && (
            <button onClick={analyse} disabled={loading} className={`ai-analyse-btn${loading ? ' ai-analyse-btn--loading' : ''}`}>
              {loading
                ? <><Loader size={16} className="ai-spin" /> {t('aiAdvisor.analyzing')}</>
                : t('aiAdvisor.analyzeBtn')}
            </button>
          )}

          {!result && !loading && (
            <button onClick={() => inputRef.current?.click()} className="ai-change-btn">
              <RefreshCw size={13} /> {t('aiAdvisor.changePhoto')}
            </button>
          )}
          {/* capture="environment" removed here too — see the drop zone above. */}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={onInputChange}
          />
        </div>
      )}

      {/* Error */}
      {error && <div className="ai-error">{error}</div>}

      {/* Results */}
      {result && (
        <div className="ai-results">
          <div className="ai-results-header">
            <h3 className="ai-results-title">{t('aiAdvisor.resultsTitle')}</h3>
            {result.hairAnalysis && (
              <p className="ai-results-analysis">
                {result.hairAnalysis.color} · {result.hairAnalysis.texture} · {result.hairAnalysis.length}
              </p>
            )}
          </div>

          <div className="ai-cards-grid">
            {result.recommendations.map((rec, i) => (
              <RecommendationCard key={rec.productId} rec={rec} rank={i + 1} isEl={isEl} t={t} />
            ))}
          </div>

          <p className="ai-disclaimer">{t('aiAdvisor.disclaimer')}</p>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button onClick={clearPhoto} className="ai-change-btn">
              <Camera size={13} /> {t('aiAdvisor.uploadPrompt')}
            </button>
          </div>
        </div>
      )}

      <Styles />
    </div>
  );
}

function RecommendationCard({ rec, rank, isEl, t }) {
  const p = rec.product;
  const img = p.image_urls?.[0] || PLACEHOLDER_IMG;
  const reason = isEl ? rec.reasonEl : rec.reasonEn;

  return (
    <div className="ai-card">
      <div className="ai-card-img-wrap">
        <img src={img} alt={p.name} className="ai-card-img" />
        <div className="ai-rank-badge">#{rank}</div>
        <div className="ai-match-badge">{rec.matchScore}% {t('aiAdvisor.matchLabel')}</div>
      </div>
      <div className="ai-card-body">
        <h4 className="ai-card-name">{p.name}</h4>
        <p className="ai-card-meta">{t(`catalog.color.${p.color_key}`, { defaultValue: p.color_key })} · {p.length_cm}cm</p>
        <p className="ai-card-reason">{reason}</p>
        {/* "/day" was hardcoded English on a screen whose default locale is Greek. */}
        <div className="ai-card-price">
          €{p.base_price_per_day}<span className="ai-card-per">{t('aiAdvisor.perDay')}</span>
        </div>
        <Link to={`/product/${p.id}`} style={{ textDecoration: 'none' }}>
          <div className="ai-view-btn">{t('aiAdvisor.viewProduct')}</div>
        </Link>
      </div>
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      /* ── Container ── */
      .ai-container {
        background: ${theme.white};
        border: 2px solid ${theme.gold};
        border-radius: 4px;
        padding: 32px 20px 28px;
        margin-bottom: 48px;
        position: relative;
      }
      @media (min-width: 640px) {
        .ai-container { padding: 48px 48px 40px; }
      }

      /* ── Close ── */
      .ai-close {
        position: absolute; top: 12px; right: 14px;
        background: none; border: none; cursor: pointer;
        color: ${theme.taupe}; font-size: 18px; line-height: 1;
        padding: 6px; min-width: 32px; min-height: 32px;
      }

      /* ── Header ── */
      .ai-header { text-align: center; margin-bottom: 28px; }
      .ai-badge-row {
        display: inline-flex; align-items: center; gap: 6px;
        color: ${theme.gold}; font-size: 11px; letter-spacing: 0.2em;
        margin-bottom: 10px;
      }
      .ai-title {
        font-family: var(--gg-font-display);
        font-size: clamp(24px, 5vw, 38px);
        font-weight: 400; color: ${theme.ink};
        margin: 0 0 8px;
      }
      .ai-subtitle {
        color: ${theme.grey500}; font-size: 14px;
        margin: 0 auto; max-width: 440px; line-height: 1.6;
      }

      /* ── Drop zone ── */
      .ai-dropzone {
        border: 2px dashed ${theme.sandMid};
        border-radius: 4px;
        padding: 44px 20px;
        text-align: center;
        cursor: pointer;
        background: ${theme.cream};
        transition: all 0.2s;
        -webkit-tap-highlight-color: transparent;
      }
      .ai-privacy-note {
        font-size: 12px; color: ${theme.grey450}; line-height: 1.7;
        margin: 0 0 14px; text-align: center;
      }
      .ai-privacy-link { color: ${theme.gold}; }
      .ai-consent {
        display: flex; gap: 10px; align-items: flex-start;
        font-size: 12.5px; color: ${theme.ink700}; line-height: 1.65;
        margin: 0 0 14px; cursor: pointer;
      }
      .ai-consent-box { margin-top: 2px; flex-shrink: 0; accent-color: ${theme.gold}; }
      /* Dimmed and un-clickable rather than removed: a drop zone that is simply
         absent until a box is ticked reads as a broken feature. This reads as a
         step not yet taken, which is what it is. */
      .ai-dropzone--locked { opacity: 0.45; cursor: not-allowed; }
      .ai-dropzone--active { border-color: ${theme.gold}; background: ${theme.cream}; }
      .ai-dropzone:focus-visible { outline: 2px solid ${theme.gold}; outline-offset: 3px; }
      .ai-dropzone-icon { color: ${theme.gold}; margin-bottom: 14px; }
      .ai-dropzone-text {
        font-family: var(--gg-font-display);
        font-size: 20px; color: ${theme.ink}; margin-bottom: 6px;
      }
      .ai-dropzone-hint { font-size: 12px; color: ${theme.grey400}; }
      @media (min-width: 640px) {
        .ai-dropzone { padding: 56px 24px; }
      }

      /* ── Preview ── */
      .ai-preview-wrap {
        display: flex; flex-direction: column;
        align-items: center; gap: 14px;
      }
      .ai-preview-inner { position: relative; display: inline-block; }
      .ai-preview-img {
        width: min(200px, 80vw); height: auto;
        min-height: 160px; max-height: 280px;
        object-fit: cover; border-radius: 4px;
        box-shadow: 0 4px 24px ${theme.alpha.black(0.12)};
      }
      .ai-preview-clear {
        position: absolute; top: 8px; right: 8px;
        background: ${theme.alpha.black(0.65)}; border: none;
        border-radius: 50%; width: 32px; height: 32px;
        display: flex; align-items: center; justify-content: center;
        color: ${theme.white}; cursor: pointer;
      }

      /* ── Buttons ── */
      .ai-analyse-btn {
        width: 100%; max-width: 380px;
        padding: 16px 24px;
        background: ${theme.ink}; color: ${theme.white};
        border: none; border-radius: 2px;
        font-size: 13px; letter-spacing: 0.15em; font-weight: 500;
        cursor: pointer; font-family: var(--gg-font-body);
        display: flex; align-items: center; justify-content: center; gap: 8px;
        transition: background 0.2s;
        min-height: 52px;
      }
      .ai-analyse-btn--loading { background: ${theme.grey240}; cursor: not-allowed; }
      .ai-change-btn {
        background: transparent; border: 1px solid ${theme.sandMid};
        border-radius: 2px; padding: 12px 24px;
        font-size: 12px; color: ${theme.grey500}; cursor: pointer;
        font-family: var(--gg-font-body); letter-spacing: 0.08em;
        display: inline-flex; align-items: center; gap: 6px;
        min-height: 44px;
      }
      @keyframes ai-spin { to { transform: rotate(360deg); } }
      .ai-spin { animation: ai-spin 1s linear infinite; }

      /* ── Error ── */
      .ai-error {
        text-align: center; margin-top: 16px;
        color: ${theme.redBrick}; font-size: 13px;
        padding: 10px; border-radius: 2px;
        background: ${theme.redWashPale};
      }

      /* ── Results ── */
      .ai-results { margin-top: 36px; }
      .ai-results-header { text-align: center; margin-bottom: 24px; }
      .ai-results-title {
        font-family: var(--gg-font-display);
        font-size: clamp(22px, 4vw, 28px);
        font-weight: 400; color: ${theme.ink}; margin: 0 0 6px;
      }
      .ai-results-analysis { color: ${theme.grey500}; font-size: 13px; margin: 0; line-height: 1.5; }
      .ai-disclaimer {
        text-align: center; color: ${theme.taupe};
        font-size: 11px; margin-top: 20px; letter-spacing: 0.05em;
      }

      /* ── Cards grid ── */
      .ai-cards-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
      }
      @media (min-width: 540px) {
        .ai-cards-grid { grid-template-columns: repeat(2, 1fr); }
      }
      @media (min-width: 860px) {
        .ai-cards-grid { grid-template-columns: repeat(3, 1fr); }
      }

      /* ── Card ── */
      .ai-card {
        background: ${theme.cream}; border-radius: 4px;
        overflow: hidden; border: 1px solid ${theme.creamBone};
      }
      .ai-card-img-wrap { position: relative; height: 200px; overflow: hidden; }
      .ai-card-img { width: 100%; height: 100%; object-fit: cover; }
      .ai-rank-badge {
        position: absolute; top: 10px; left: 10px;
        background: ${theme.ink}; color: ${theme.gold};
        padding: 4px 10px; border-radius: 100px;
        font-size: 12px; font-weight: 600; letter-spacing: 0.05em;
      }
      .ai-match-badge {
        position: absolute; bottom: 10px; right: 10px;
        background: ${theme.alpha.gold(0.92)}; color: ${theme.white};
        padding: 5px 12px; border-radius: 100px;
        font-size: 11px; letter-spacing: 0.08em;
      }
      .ai-card-body { padding: 16px 18px 18px; }
      .ai-card-name {
        font-family: var(--gg-font-display);
        font-size: 20px; font-weight: 500;
        color: ${theme.ink}; margin: 0 0 4px;
      }
      .ai-card-meta { color: ${theme.grey400}; font-size: 12px; margin: 0 0 10px; }
      .ai-card-reason { color: ${theme.grey600}; font-size: 13px; margin: 0 0 14px; line-height: 1.55; }
      .ai-card-price {
        font-family: var(--gg-font-display);
        font-size: 22px; color: ${theme.ink}; margin-bottom: 14px;
      }
      .ai-card-per { font-size: 12px; color: ${theme.grey400}; font-family: var(--gg-font-body); }
      .ai-view-btn {
        text-align: center; padding: 14px;
        background: ${theme.ink}; color: ${theme.white};
        border-radius: 2px; font-size: 11px;
        letter-spacing: 0.15em; font-weight: 500;
      }

      /* ── Tease (collapsed) ── */
      .ai-tease-wrap { text-align: center; margin-bottom: 48px; }
      .ai-tease-btn {
        display: inline-flex; align-items: center; gap: 10px;
        background: ${theme.white}; border: 1px solid ${theme.creamBone}; border-radius: 4px;
        padding: 14px 24px; cursor: pointer;
        box-shadow: 0 2px 12px ${theme.alpha.black(0.06)};
        max-width: 100%;
      }
      .ai-tease-title {
        font-family: var(--gg-font-display);
        font-size: 17px; color: ${theme.ink}; font-weight: 500;
      }
      .ai-tease-badge { font-size: 11px; color: ${theme.gold}; letter-spacing: 0.15em; }
    `}</style>
  );
}
